import styled from '@emotion/styled';
import { Check, Info as InfoIcon, ExclamationMark, X } from '@phosphor-icons/react';
import { capitalizeFirstLetter } from 'utils';
import { Text, TextProps } from 'components/text/Text';
import type { ToastProps, SnackbarAction } from 'modules/notifications/types';
import { t } from '@lingui/macro';
import { Codes } from 'lib/api';
import { Box } from '@mui/material';
import type { BoxProps } from '@mui/material/Box';

/**
 * Toast component, part of Delogue design system.
 * Toasts should be opened with the useNotification hook, not by using this component directly
 */
export const Toast = ({
  id,
  variant = 'info',
  message,
  header,
  actions = [{ id: 'close', label: t`Close` }],
  onClose,
  isCode,
  messageList,
}: ToastProps) => {
  const onClickButton = (action: SnackbarAction) => {
    if (action.onClick) {
      action.onClick(action.id);
      return;
    }
    if (onClose) {
      onClose(action.id, id);
    }
  };

  // Use variant as default header if no header is provided
  const displayHeader = header || capitalizeFirstLetter(variant);

  return (
    <Row data-testid="toast_container">
      <IconCircle>
        <Icon variant={variant} />
      </IconCircle>
      <Content sx={{ flex: 1 }}>
        <Column>
          <HeadingText variant="bodyMd" weight="semiBold">
            <Codes capitalize text={displayHeader} />
          </HeadingText>
          {message &&
            (isCode ? (
              <TextFlex variant="bodySm" weight="regular">
                <Codes text={message} />
              </TextFlex>
            ) : (
              <TextFlex variant="bodySm" weight="regular">
                {message}
              </TextFlex>
            ))}
          {messageList && (
            <StyledList>
              {messageList?.map(
                item =>
                  (item?.code || item?.msg) && (
                    <StyledListItem key={`error-list-item-${item?.code ?? item?.msg}`}>
                      {/* Prefer the backend's specific message (e.g. "Please select valid and
                          active category") over the generic code/type; fall back to the
                          code→friendly mapping when no message is provided (DL-60176). */}
                      {item?.msg ? (
                        <TextFlex variant="bodySm" weight="regular">
                          {item?.msg}
                        </TextFlex>
                      ) : (
                        <TextFlex variant="bodySm" weight="regular">
                          <Codes text={item?.code} />
                        </TextFlex>
                      )}
                    </StyledListItem>
                  )
              )}
            </StyledList>
          )}
        </Column>
        {actions?.length ? (
          <Content sx={{ alignItems: 'center' }}>
            {actions.map(action => (
              <TextFlex
                role="button"
                tabIndex={0}
                color={variant === 'base' ? 'inherit' : 'primaryOnBrand'}
                variant="labelSm"
                weight="semiBold"
                key={action.id}
                data-testid={`toast_action_${action.id}`}
                onClick={() => onClickButton(action)}
                style={{ cursor: 'pointer' }}
              >
                {action.label}
              </TextFlex>
            ))}
          </Content>
        ) : null}
      </Content>
    </Row>
  );
};

const TextFlex = styled(Text)`
  flex: 1;
  font-size: 0.875rem;
`;

type IconProps = {
  variant: string;
  color?: string;
};

const Icon = ({ variant, color = 'white' }: IconProps) => {
  switch (variant) {
    case 'success':
      return <Check size={16} color={color} />;
    case 'info':
      return <InfoIcon size={16} color={color} />;
    case 'warning':
      return <ExclamationMark size={16} color={color} />;
    case 'error':
      return <X size={16} color={color} />;
    default:
      return null;
  }
};

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-right: 8px;
  gap: 8px;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${p => p.theme.palette.text.primaryOnBrand};
`;

const Content = styled((props: BoxProps) => <Box {...props} />)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  fontFamily: theme.typography.fontFamily,
}));
const HeadingText = styled((props: TextProps) => <Text {...props} />)(({ theme }) => ({
  fontSize: theme.typography.htmlFontSize,
}));

const Row = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StyledListItem = styled.li`
  position: relative;
  padding-left: 16px;
  margin: 2px 0;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    display: inline-block;
    width: 8px;
    text-align: center;
    line-height: 1;
  }
`;
